import React, { useState, useEffect } from "react";
import { Check, Copy, Loader2, Save, Trash2 as TrashIcon } from "lucide-react";
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getIngredients } from "../api/ingredients";
import { createQuotation } from "../api/quotationsApi";

/* ------------------------------------------------------------------ */
/* Tiny inline icons                                                    */
/* ------------------------------------------------------------------ */
const PlusIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="13" height="13" {...props}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const InfoIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Reference Data for Lists!A:A and Lists!B:B (Clients -> PL Category)*/
/* ------------------------------------------------------------------ */
const PL_CLIENTS_DB = [
  { client: "Signature Jar", category: "Signature Items" },
  { client: "Youngevity", category: "Pouches" },
  { client: "Baker PL", category: "Baking" },
];

const BULK_PACK_TYPES = {
  "Bulk Master Case": { cs: 25, unit: "kg", multiplier: 1.0 },
  "Bulk 10 kg": { cs: 10, unit: "kg", multiplier: 1.05 },
  "Bulk 1 kg": { cs: 1, unit: "kg", multiplier: 1.15 },
};

const PACKAGING_CATALOG = {
  "Ergo Jar 212ml": { unitsPerCase: 6, netWeight: 140, unit: "g", packagingCost: 1.2 },
  "6 oz Round Glass": { unitsPerCase: 12, netWeight: 150, unit: "g", packagingCost: 0.95 },
  "Resealable Stand": { unitsPerCase: 150, netWeight: 90, unit: "g", packagingCost: 0.18 },
  "Pillow Bag Small": { unitsPerCase: 200, netWeight: 10, unit: "g", packagingCost: 0.12 },
  "PL-6 Round Bottle": { unitsPerCase: 100, netWeight: 100, unit: "g", packagingCost: 0.85 },
};

const DEFAULT_MARGIN_TIERS = {
  "Ergo Jar 212ml": 15,
  "6 oz Round Glass": 16,
  "Resealable Stand": 16,
  "Pillow Bag Small": 20,
  "PL-6 Round Bottle": 17,
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
const fmt = (v) => `$${Number(v).toFixed(2)}`;
const roundCharm = (v) => Math.ceil(v * 20) / 20;

let plRowId = 0;
const newBulkRow = () => ({
  id: `plb-${plRowId++}`,
  client: "",
  sku: "",
  packagingType: "Bulk Master Case",
  cs: 25,
  premium: "No",
  minimum: "No",
  unitCadOverride: "",
});
const newRetailRow = () => ({
  id: `plr-${plRowId++}`,
  client: "",
  sku: "",
  packagingType: "",
  loosePacking: "No",
  unitsPerCase: "",
  netWeight: "",
  netWeightUnit: "g",
  allergenPremium: "No",
  coPacking: "No",
  coPackFeeOverride: "",
});

/* ------------------------------------------------------------------ */
/* Shared style fragments                                              */
/* ------------------------------------------------------------------ */
const inputCls =
  "px-3 py-2 rounded-lg text-sm outline-none border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-black focus:border-transparent transition-colors w-full";
const thCls = "px-3 py-3 text-left font-semibold text-gray-700 whitespace-nowrap text-xs align-bottom";
const tdCls = "px-3 py-2 align-top";

/* ------------------------------------------------------------------ */
/* Header Component (Recipe Builder Style)                              */
/* ------------------------------------------------------------------ */
const HeaderWithApply = ({ title, colKey, copiedCol, onCopy, onApply, applyType = "none", options = [], placeholder = "" }) => {
  return (
    <div className="flex flex-col gap-1 w-full min-w-[5rem]">
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-gray-700">{title}</span>
        {onCopy && (
          <button
            type="button"
            onClick={() => onCopy(colKey)}
            className="text-gray-400 hover:text-gray-700 transition-colors"
            title={`Copy ${title} column`}
          >
            {copiedCol === colKey ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
        {applyType === "select" && (
          <select
            className="ml-auto px-1.5 py-0.5 text-xs font-normal border border-gray-300 rounded bg-white text-gray-700 outline-none focus:ring-1 focus:ring-black"
            onChange={(e) => {
              if (e.target.value) {
                onApply(e.target.value);
                e.target.value = "";
              }
            }}
          >
            <option value="">{placeholder || "Set all..."}</option>
            {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
          </select>
        )}
      </div>
      {(applyType === "text" || applyType === "number") && (
        <input
          type={applyType}
          placeholder={placeholder || "Apply all (Enter)"}
          className="mt-1 w-full px-2 py-1 text-xs font-normal border border-gray-300 rounded bg-gray-50 text-gray-700 outline-none focus:ring-1 focus:ring-black"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.value) {
              onApply(e.target.value);
              e.target.value = "";
            }
          }}
        />
      )}
    </div>
  );
};

const selectCls = `${inputCls} appearance-none bg-no-repeat pr-8`;
const selectStyle = {
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
  backgroundPosition: "right 0.5rem center",
  backgroundSize: "1em",
};

/* ------------------------------------------------------------------ */
/* Bulk calculator                                                      */
/* ------------------------------------------------------------------ */
function BulkCalculator({ rmDatabase }) {
  const [rows, setRows] = useState([newBulkRow()]);
  const [targetMargin, setTargetMargin] = useState(20);
  const [copiedCol, setCopiedCol] = useState(null);
  const [lineCount, setLineCount] = useState(1);
  const [saving, setSaving] = useState(false);

  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addRow = (count = 1) => {
    const num = Math.max(1, Math.min(100, parseInt(count, 10) || 1));
    const newRows = Array.from({ length: num }, () => newBulkRow());
    setRows((prev) => [...prev, ...newRows]);
  };
  const clearAll = () => setRows([newBulkRow()]);
  const applyToAll = (key, val) => setRows((prev) => prev.map(r => ({ ...r, [key]: val })));

  const handleCopyCol = async (colKey, getVal) => {
    const text = rows.map(r => getVal(r)).join("\r\n");
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCol(colKey);
      setTimeout(() => setCopiedCol(null), 2000);
    } catch (err) { }
  };

  const handlePaste = (e, index) => {
    const pasteData = e.clipboardData.getData("text");
    if (!pasteData) return;
    const tokens = pasteData.split(/[\r\n\t,]+/).map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;

    e.preventDefault();
    const matched = tokens.map(token => {
      const found = rmDatabase.find(r => r.sku === token || (r.name || "").toLowerCase() === token.toLowerCase());
      return found ? found.sku : token;
    });

    setRows(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], sku: matched[0] };
      const extras = matched.slice(1).map(m => ({
        ...newBulkRow(),
        sku: m,
        client: newRows[index].client,
        packagingType: newRows[index].packagingType
      }));
      newRows.splice(index + 1, 0, ...extras);
      return newRows;
    });
  };

  const resolveClientCategory = (clientName) => {
    if (!clientName) return "";
    const match = PL_CLIENTS_DB.find(c => c.client === clientName);
    return match ? (match.category || "CREATE NEW") : "CREATE NEW";
  };

  const handleSaveBulkQuotation = async () => {
    const validRows = rows.filter(r => r.sku && r.client);
    if (validRows.length === 0) {
      toast.error("Please add at least one row with a Client and SKU.");
      return;
    }

    setSaving(true);
    try {
      const customerName = validRows[0].client;
      let totalAmount = 0;
      const items = validRows.map(r => {
        const rm = rmDatabase.find((c) => c.sku === r.sku);
        const packInfo = BULK_PACK_TYPES[r.packagingType];
        const baseCost = rm ? (rm.unitCost || 0) * (packInfo?.multiplier ?? 1) : null;
        const premiumAdd = r.premium === "Yes" ? 0.75 : 0;
        const minimumAdd = r.minimum === "Yes" ? 0.3 : 0;
        const unitCad =
          r.unitCadOverride !== ""
            ? Number(r.unitCadOverride)
            : baseCost != null
              ? Math.round((baseCost + premiumAdd + minimumAdd) * 100) / 100
              : null;
        const bulkPerKg = unitCad != null ? roundCharm(unitCad / (1 - targetMargin / 100)) : null;
        const csVal = Number(r.cs) || packInfo?.cs || 0;
        const masterCs = bulkPerKg != null ? bulkPerKg * csVal : null;

        totalAmount += masterCs || 0;

        return {
          sku: r.sku,
          productName: rm?.name || r.sku,
          netWeight: String(csVal),
          packaging: r.packagingType || 'Bulk',
          unitsPerCase: 1,
          pricePerUnit: masterCs,
          calculatedPrice: masterCs
        };
      });

      await createQuotation({
        customerName,
        language: 'en',
        items,
        totalAmount
      });

      toast.success("Bulk Quotation saved successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save bulk quotation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900">Private Label Calculator — Bulk</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Target margin %:</span>
            <input
              type="number"
              value={targetMargin}
              onChange={(e) => setTargetMargin(Math.max(0, Math.min(95, Number(e.target.value) || 0)))}
              className={`${inputCls} w-20 text-center !py-1.5`}
            />
          </div>
          <button
            onClick={clearAll}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors shadow-sm"
          >
            <TrashIcon size={16} /> Clear All
          </button>
          <button
            onClick={handleSaveBulkQuotation}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
          >
            <Save size={16} /> {saving ? "Saving..." : "Save Quotation"}
          </button>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="1"
              max="100"
              value={lineCount}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setLineCount(isNaN(val) ? "" : Math.max(1, val));
              }}
              onBlur={() => {
                if (!lineCount || lineCount < 1) setLineCount(1);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") addRow(Number(lineCount) || 1);
              }}
              className={`${inputCls} w-16 text-center !py-1.5`}
              placeholder="1"
              title="Number of lines to add"
            />
            <button
              onClick={() => addRow(Number(lineCount) || 1)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-black text-white hover:bg-gray-800 transition-colors shadow-sm whitespace-nowrap"
            >
              <PlusIcon /> Add Line
            </button>
          </div>
        </div>
      </div>
      <p className="text-sm mb-6 flex items-center gap-2 text-gray-600 bg-gray-50 p-3.5 rounded-xl border border-gray-200">
        <InfoIcon />
        Pick a client, an item, and a bulk pack size — cost, premium and minimum surcharges roll into the quoted price automatically.
      </p>

      <div className="overflow-x-auto border-t border-gray-200 -mx-6 px-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/50">
              <th className={thCls}>
                <HeaderWithApply
                  title="Client" colKey="client" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.client)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="PL Category" colKey="plCategory" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => resolveClientCategory(r.client))}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Ingredient Name" colKey="ingredientName" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => rmDatabase.find(db => db.sku === r.sku)?.name || "")}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="SKU" colKey="sku" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.sku)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Packaging" colKey="packagingType" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.packagingType)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="CS" colKey="cs" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.cs)}
                  onApply={(val) => applyToAll("cs", val)}
                  applyType="number" placeholder="Set all"
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Unit" colKey="unit" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => BULK_PACK_TYPES[r.packagingType]?.unit || "kg")}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Premium" colKey="premium" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.premium)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Minimum" colKey="minimum" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.minimum)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Unit CAD Override" colKey="unitCadOverride" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.unitCadOverride)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Bulk / kg" colKey="bulkPerKg" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => {
                    const rm = rmDatabase.find((c) => c.sku === r.sku);
                    const packInfo = BULK_PACK_TYPES[r.packagingType];
                    const baseCost = rm ? rm.unitCost * (packInfo?.multiplier ?? 1) : null;
                    const unitCad = r.unitCadOverride !== "" ? Number(r.unitCadOverride) : baseCost != null ? Math.round((baseCost + (r.premium === "Yes" ? 0.75 : 0) + (r.minimum === "Yes" ? 0.3 : 0)) * 100) / 100 : null;
                    return unitCad != null ? roundCharm(unitCad / (1 - targetMargin / 100)) : "";
                  })}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply title="/ Master CS" />
              </th>
              <th className={thCls}>Margin %</th>
              <th className={`${thCls} text-right`}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const rm = rmDatabase.find((r) => r.sku === row.sku);
              const packInfo = BULK_PACK_TYPES[row.packagingType];
              const baseCost = rm ? (rm.unitCost || 0) * (packInfo?.multiplier ?? 1) : null;
              const premiumAdd = row.premium === "Yes" ? 0.75 : 0;
              const minimumAdd = row.minimum === "Yes" ? 0.3 : 0;
              const unitCad =
                row.unitCadOverride !== ""
                  ? Number(row.unitCadOverride)
                  : baseCost != null
                    ? Math.round((baseCost + premiumAdd + minimumAdd) * 100) / 100
                    : null;
              const bulkPerKg = unitCad != null ? roundCharm(unitCad / (1 - targetMargin / 100)) : null;
              const csVal = Number(row.cs) || packInfo?.cs || 0;
              const masterCs = bulkPerKg != null ? bulkPerKg * csVal : null;
              const marginPct = bulkPerKg != null && unitCad != null ? ((bulkPerKg - unitCad) / bulkPerKg) * 100 : null;
              const category = resolveClientCategory(row.client);

              return (
                <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                  <td className={tdCls}>
                    <select
                      value={row.client}
                      onChange={(e) => updateRow(row.id, { client: e.target.value })}
                      className={`${selectCls} min-w-[11rem]`}
                      style={selectStyle}
                    >
                      <option value="">Select...</option>
                      {PL_CLIENTS_DB.map(c => <option key={c.client} value={c.client}>{c.client}</option>)}
                      <option value="CREATE NEW">CREATE NEW</option>
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input
                      value={category}
                      readOnly
                      placeholder="Category"
                      className={`${inputCls} min-w-[9rem] bg-gray-100 text-gray-500 cursor-not-allowed`}
                    />
                  </td>
                  <td className={tdCls}>
                    <select
                      value={row.sku || ""}
                      onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                      className={`${selectCls} min-w-[14rem]`}
                      style={selectStyle}
                    >
                      <option value="">Select ingredient...</option>
                      {rmDatabase.map(r => <option key={r.sku} value={r.sku}>{r.name}</option>)}
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input
                      value={row.sku}
                      onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                      onPaste={(e) => handlePaste(e, idx)}
                      placeholder="Paste or type SKU"
                      className={`${inputCls} font-mono min-w-[11rem] border-blue-200 focus:border-blue-500`}
                    />
                  </td>
                  <td className={tdCls}>
                    <select
                      value={row.packagingType}
                      onChange={(e) => updateRow(row.id, { packagingType: e.target.value, cs: BULK_PACK_TYPES[e.target.value]?.cs || 0 })}
                      className={`${selectCls} min-w-[10rem]`}
                      style={selectStyle}
                    >
                      <option value="">Select packaging…</option>
                      {Object.keys(BULK_PACK_TYPES).map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input type="number" value={row.cs} onChange={(e) => updateRow(row.id, { cs: e.target.value })} className={`${inputCls} w-20 font-mono`} />
                  </td>
                  <td className={`${tdCls} text-sm text-gray-600 pt-4`}>{packInfo?.unit || "kg"}</td>
                  <td className={tdCls}>
                    <select value={row.premium} onChange={(e) => updateRow(row.id, { premium: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  </td>
                  <td className={tdCls}>
                    <select value={row.minimum} onChange={(e) => updateRow(row.id, { minimum: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input
                      type="number"
                      value={row.unitCadOverride}
                      onChange={(e) => updateRow(row.id, { unitCadOverride: e.target.value })}
                      placeholder={unitCad != null ? unitCad.toFixed(2) : "0.00"}
                      className={`${inputCls} w-24 font-mono`}
                    />
                  </td>
                  <td className={`${tdCls} font-mono text-sm font-semibold text-gray-900 pt-4`}>{bulkPerKg != null ? fmt(bulkPerKg) : "—"}</td>
                  <td className={`${tdCls} font-mono text-sm text-gray-800 pt-4`}>{masterCs != null ? fmt(masterCs) : "—"}</td>
                  <td className={`${tdCls} pt-3`}>
                    {marginPct != null ? (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 whitespace-nowrap">
                        {marginPct.toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className={`${tdCls} text-right pt-3`}>
                    <button
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length <= 1}
                      className={`p-2 rounded-lg transition-colors ${rows.length <= 1 ? 'opacity-50 text-red-500 cursor-not-allowed' : 'hover:bg-red-50 text-red-500'}`}
                    >
                      <TrashIcon size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Retail packaging calculator                                         */
/* ------------------------------------------------------------------ */
function RetailPackagingCalculator({ rmDatabase }) {
  const [rows, setRows] = useState([newRetailRow()]);
  const [marginTiers, setMarginTiers] = useState(DEFAULT_MARGIN_TIERS);
  const [quoteVolume, setQuoteVolume] = useState(20);
  const [currency, setCurrency] = useState("CAD");
  const [copiedCol, setCopiedCol] = useState(null);
  const [lineCount, setLineCount] = useState(1);
  const [saving, setSaving] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.quotationData) {
      const q = location.state.quotationData;
      if (q.items && q.items.length > 0) {
        setRows(q.items.map((item, idx) => ({
          ...newRetailRow(),
          id: `plr-${Date.now()}-${idx}`,
          sku: item.sku,
          netWeight: item.netWeight,
          packagingType: item.packaging,
          unitsPerCase: item.unitsPerCase,
        })));
        if (q.customerName) {
          setRows(prev => prev.map(r => ({ ...r, client: q.customerName })));
        }
      }
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const handleSaveQuotation = async () => {
    const validRows = rows.filter(r => r.sku && r.client);
    if (validRows.length === 0) {
      toast.error("Please add at least one row with a Client and SKU.");
      return;
    }

    setSaving(true);
    try {
      const customerName = validRows[0].client;
      let totalAmount = 0;
      const items = validRows.map(r => {
        const rm = rmDatabase.find((c) => c.sku === r.sku);
        const pack = PACKAGING_CATALOG[r.packagingType];
        const netWeight = Number(r.netWeight) || 0;
        const costPerGram = rm ? (rm.unitCost || 0) / 1000 : 0;
        const rawCost = rm ? costPerGram * netWeight : null;
        const packagingCost = pack?.packagingCost ?? 0;
        const coPackFee = r.coPacking === "Yes" ? (r.coPackFeeOverride !== "" ? Number(r.coPackFeeOverride) : 0) : (r.loosePacking === "Yes" ? 0.15 : 0);
        const allergenAdd = r.allergenPremium === "Yes" ? 0.1 : 0;
        const finalCogsUnit = rawCost != null ? Math.round((rawCost + packagingCost + coPackFee + allergenAdd) * 100) / 100 : null;
        const unitsPerCase = Number(r.unitsPerCase) || pack?.unitsPerCase || 0;
        const margin = marginTiers[r.packagingType] ?? 18;
        const retailUnit = finalCogsUnit != null ? roundCharm(finalCogsUnit / (1 - margin / 100)) : null;
        const calculatedPrice = retailUnit != null ? retailUnit * unitsPerCase : null;

        totalAmount += calculatedPrice || 0;

        return {
          sku: r.sku,
          productName: rm?.name || r.sku,
          netWeight: String(r.netWeight),
          packaging: r.packagingType,
          unitsPerCase,
          pricePerUnit: retailUnit,
          calculatedPrice
        };
      });

      await createQuotation({
        customerName,
        language: 'en',
        items,
        totalAmount
      });

      toast.success("Quotation saved successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save quotation.");
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addRow = (count = 1) => {
    const num = Math.max(1, Math.min(100, parseInt(count, 10) || 1));
    const newRows = Array.from({ length: num }, () => newRetailRow());
    setRows((prev) => [...prev, ...newRows]);
  };
  const clearAll = () => setRows([newRetailRow()]);
  const applyToAll = (key, val) => setRows((prev) => prev.map(r => ({ ...r, [key]: val })));

  const handleCopyCol = async (colKey, getVal) => {
    const text = rows.map(r => getVal(r)).join("\r\n");
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCol(colKey);
      setTimeout(() => setCopiedCol(null), 2000);
    } catch (err) { }
  };

  const applyPackagingDefaults = (id, type) => {
    const pack = PACKAGING_CATALOG[type];
    if (!pack) return updateRow(id, { packagingType: type });
    updateRow(id, { packagingType: type, unitsPerCase: pack.unitsPerCase, netWeightUnit: pack.unit });
  };

  const handlePaste = (e, index) => {
    const pasteData = e.clipboardData.getData("text");
    if (!pasteData) return;
    const tokens = pasteData.split(/[\r\n\t,]+/).map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;

    e.preventDefault();
    const matched = tokens.map(token => {
      const found = rmDatabase.find(r => r.sku === token || (r.name || "").toLowerCase() === token.toLowerCase());
      return found ? found.sku : token;
    });

    setRows(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], sku: matched[0] };
      const extras = matched.slice(1).map(m => ({
        ...newRetailRow(),
        sku: m,
        client: newRows[index].client,
        packagingType: newRows[index].packagingType
      }));
      newRows.splice(index + 1, 0, ...extras);
      return newRows;
    });
  };

  const resolveClientCategory = (clientName) => {
    if (!clientName) return "";
    const match = PL_CLIENTS_DB.find(c => c.client === clientName);
    return match ? (match.category || "CREATE NEW") : "CREATE NEW";
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900">Private Label Calculator — Retail Packaging</h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm whitespace-nowrap font-medium text-gray-700">Quote Volume:</span>
              <input
                type="number"
                value={quoteVolume}
                onChange={(e) => setQuoteVolume(Number(e.target.value) || 0)}
                className={`${inputCls} w-28 text-center font-mono !py-1.5`}
              />
              <span className="text-sm text-gray-500">units</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Currency:</span>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${inputCls} !py-1.5`}>
                <option>CAD</option>
                <option>USD</option>
                <option>EUR</option>
              </select>
            </div>
            <button
              onClick={clearAll}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors shadow-sm"
            >
              <TrashIcon size={16} /> Clear All
            </button>
            <button
              onClick={handleSaveQuotation}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
            >
              <Save size={16} /> {saving ? "Saving..." : "Save Quotation"}
            </button>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="1"
                max="100"
                value={lineCount}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setLineCount(isNaN(val) ? "" : Math.max(1, val));
                }}
                onBlur={() => {
                  if (!lineCount || lineCount < 1) setLineCount(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addRow(Number(lineCount) || 1);
                }}
                className={`${inputCls} w-16 text-center !py-1.5`}
                placeholder="1"
                title="Number of lines to add"
              />
              <button
                onClick={() => addRow(Number(lineCount) || 1)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-black text-white hover:bg-gray-800 transition-colors shadow-sm whitespace-nowrap"
              >
                <PlusIcon /> Add Line
              </button>
            </div>
          </div>
        </div>
        <p className="text-sm mb-6 flex items-center gap-2 text-gray-600 bg-gray-50 p-3.5 rounded-xl border border-gray-200">
          <InfoIcon />
          Pick a packaging type to auto-fill Units/CS and Net Weight from Packaging Setup, then the target margin from PL Margin Tiers computes the retail price.
        </p>

        <div className="overflow-x-auto border-t border-gray-200 -mx-6 px-6 pb-4">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className={thCls}>
                  <HeaderWithApply
                    title="Client" colKey="client" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.client)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="PL Category" colKey="plCategory" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => resolveClientCategory(r.client))}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Ingredient Name" colKey="ingredientName" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => rmDatabase.find(db => db.sku === r.sku)?.name || "")}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="SKU" colKey="sku" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.sku)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Packaging" colKey="packagingType" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.packagingType)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Loose Packing?" colKey="loosePacking" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.loosePacking)}
                    onApply={(val) => applyToAll("loosePacking", val)}
                    applyType="select" options={["Yes", "No"]}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Units/CS" colKey="unitsPerCase" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.unitsPerCase)}
                    onApply={(val) => applyToAll("unitsPerCase", val)}
                    applyType="number" placeholder="Set all"
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Net Weight" colKey="netWeight" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.netWeight)}
                    onApply={(val) => applyToAll("netWeight", val)}
                    applyType="number" placeholder="Set all"
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Allergen Premium" colKey="allergenPremium" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.allergenPremium)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Co-Packing?" colKey="coPacking" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.coPacking)}
                    onApply={(val) => applyToAll("coPacking", val)}
                    applyType="select" options={["Yes", "No"]}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Co-Pack Fee/Unit" colKey="coPackFeeOverride" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.coPackFeeOverride)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="FINAL COGS/Unit" colKey="finalCogsUnit" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => {
                      const rm = rmDatabase.find((c) => c.sku === r.sku);
                      const pack = PACKAGING_CATALOG[r.packagingType];
                      // getIngredients returns unitCost per kg, so divide by 1000 for per gram
                      const costPerGram = rm ? (rm.unitCost || 0) / 1000 : 0;
                      const rawCost = rm ? costPerGram * (Number(r.netWeight) || 0) : null;
                      const packagingCost = pack?.packagingCost ?? 0;
                      const coPackFee = r.coPacking === "Yes" ? (r.coPackFeeOverride !== "" ? Number(r.coPackFeeOverride) : 0) : (r.loosePacking === "Yes" ? 0.15 : 0);
                      return rawCost != null ? Math.round((rawCost + packagingCost + coPackFee + (r.allergenPremium === "Yes" ? 0.1 : 0)) * 100) / 100 : "";
                    })}
                  />
                </th>
                <th className={thCls}>FINAL COGS/CS</th>
                <th className={thCls}>PL Retail/Unit</th>
                <th className={thCls}>PL Retail/CS</th>
                <th className={thCls}>Profit Margin %</th>
                <th className={`${thCls} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const rm = rmDatabase.find((r) => r.sku === row.sku);
                const pack = PACKAGING_CATALOG[row.packagingType];
                const netWeight = Number(row.netWeight) || 0;
                // getIngredients returns unitCost per kg, so divide by 1000 for per gram
                const costPerGram = rm ? (rm.unitCost || 0) / 1000 : 0;
                const rawCost = rm ? costPerGram * netWeight : null;
                const packagingCost = pack?.packagingCost ?? 0;
                const coPackFee = row.coPacking === "Yes" ? (row.coPackFeeOverride !== "" ? Number(row.coPackFeeOverride) : 0) : (row.loosePacking === "Yes" ? 0.15 : 0);
                const allergenAdd = row.allergenPremium === "Yes" ? 0.1 : 0;
                const finalCogsUnit = rawCost != null ? Math.round((rawCost + packagingCost + coPackFee + allergenAdd) * 100) / 100 : null;
                const unitsPerCase = Number(row.unitsPerCase) || pack?.unitsPerCase || 0;
                const finalCogsCs = finalCogsUnit != null ? finalCogsUnit * unitsPerCase : null;
                const margin = marginTiers[row.packagingType] ?? 18;
                const retailUnit = finalCogsUnit != null ? roundCharm(finalCogsUnit / (1 - margin / 100)) : null;
                const retailCs = retailUnit != null ? retailUnit * unitsPerCase : null;
                const profitMarginPct = retailUnit != null && finalCogsUnit != null ? ((retailUnit - finalCogsUnit) / retailUnit) * 100 : null;
                const category = resolveClientCategory(row.client);

                return (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                    <td className={tdCls}>
                      <select
                        value={row.client}
                        onChange={(e) => updateRow(row.id, { client: e.target.value })}
                        className={`${selectCls} min-w-[11rem]`}
                        style={selectStyle}
                      >
                        <option value="">Select client...</option>
                        {PL_CLIENTS_DB.map(c => <option key={c.client} value={c.client}>{c.client}</option>)}
                        <option value="CREATE NEW">CREATE NEW</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <input
                        value={category}
                        readOnly
                        placeholder="Category"
                        className={`${inputCls} min-w-[9rem] bg-gray-100 text-gray-500 cursor-not-allowed`}
                      />
                    </td>
                    <td className={tdCls}>
                      <select
                        value={row.sku || ""}
                        onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                        className={`${selectCls} min-w-[14rem]`}
                        style={selectStyle}
                      >
                        <option value="">Select ingredient...</option>
                        {rmDatabase.map(r => <option key={r.sku} value={r.sku}>{r.name}</option>)}
                      </select>
                    </td>
                    <td className={tdCls}>
                      <input
                        value={row.sku}
                        onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                        onPaste={(e) => handlePaste(e, idx)}
                        placeholder="Paste or type SKU"
                        className={`${inputCls} font-mono min-w-[11rem] border-blue-200 focus:border-blue-500`}
                      />
                    </td>
                    <td className={tdCls}>
                      <select
                        value={row.packagingType}
                        onChange={(e) => applyPackagingDefaults(row.id, e.target.value)}
                        className={`${selectCls} min-w-[11rem]`}
                        style={selectStyle}
                      >
                        <option value="">Select packaging…</option>
                        {Object.keys(PACKAGING_CATALOG).map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                    </td>
                    <td className={tdCls}>
                      <select value={row.loosePacking} onChange={(e) => updateRow(row.id, { loosePacking: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <input
                        type="number"
                        value={row.unitsPerCase}
                        onChange={(e) => updateRow(row.id, { unitsPerCase: e.target.value })}
                        placeholder={pack ? String(pack.unitsPerCase) : "0"}
                        className={`${inputCls} w-20 font-mono`}
                      />
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={row.netWeight}
                          onChange={(e) => updateRow(row.id, { netWeight: e.target.value })}
                          placeholder="0"
                          className={`${inputCls} w-20 font-mono`}
                        />
                        <span className="text-sm text-gray-500">{row.netWeightUnit || "g"}</span>
                      </div>
                    </td>
                    <td className={tdCls}>
                      <select value={row.allergenPremium} onChange={(e) => updateRow(row.id, { allergenPremium: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <select value={row.coPacking} onChange={(e) => updateRow(row.id, { coPacking: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      {row.coPacking === "Yes" ? (
                        <input
                          type="number"
                          value={row.coPackFeeOverride}
                          onChange={(e) => updateRow(row.id, { coPackFeeOverride: e.target.value })}
                          placeholder="0.00"
                          className={`${inputCls} w-20 font-mono`}
                        />
                      ) : (
                        <span className="text-gray-400 pl-4">—</span>
                      )}
                    </td>
                    <td className={`${tdCls} font-mono text-sm font-semibold text-gray-900 pt-4`}>{finalCogsUnit != null ? fmt(finalCogsUnit) : "—"}</td>
                    <td className={`${tdCls} font-mono text-sm text-gray-600 pt-4`}>{finalCogsCs != null ? fmt(finalCogsCs) : "—"}</td>
                    <td className={`${tdCls} font-mono text-sm font-semibold text-gray-900 pt-4`}>{retailUnit != null ? fmt(retailUnit) : "—"}</td>
                    <td className={`${tdCls} font-mono text-sm text-gray-800 pt-4`}>{retailCs != null ? fmt(retailCs) : "—"}</td>
                    <td className={`${tdCls} pt-3`}>
                      {profitMarginPct != null ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700">
                          {profitMarginPct.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className={`${tdCls} text-right pt-3`}>
                      <button
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length <= 1}
                        className={`p-2 rounded-lg transition-colors ${rows.length <= 1 ? 'opacity-50 text-red-500 cursor-not-allowed' : 'hover:bg-red-50 text-red-500'}`}
                      >
                        <TrashIcon size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


    </>
  );
}

/* ------------------------------------------------------------------ */
/* Main exported component                                             */
/* ------------------------------------------------------------------ */
const PricePrivateLabel = () => {
  const [mode, setMode] = useState("retail");
  const [rmDatabase, setRmDatabase] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getIngredients()
      .then((data) => {
        if (mounted) {
          setRmDatabase(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load ingredients:", err);
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center bg-transparent">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <p className="text-sm text-gray-500">Loading PL Calculator Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-sans">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Private Label Calculator</h2>
        <p className="text-sm text-gray-500 mt-1">
          Bulk and retail-packaging quoting for private label clients.
        </p>
      </div>

      <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-xl w-fit border border-gray-200">
        {[
          { key: "retail", label: "Retail Packaging" },
          { key: "bulk", label: "Bulk" },
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === m.key
              ? "bg-white text-gray-900 shadow-sm border border-gray-200"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === "bulk" ? <BulkCalculator rmDatabase={rmDatabase} /> : <RetailPackagingCalculator rmDatabase={rmDatabase} />}
    </div>
  );
};

export default PricePrivateLabel;